import { useEffect, useRef, useState } from "react";
import type { Map as MapLibreMap, Marker as MapLibreMarker } from "maplibre-gl";
import { createKoreaProjection, KOREA_MAP_HEIGHT, KOREA_MAP_WIDTH, projectKorea } from "../../../geo/src/project";
import type { BBox, Cluster, LonLat } from "../types";

export type MapCanvasProps = {
  clusters: Cluster[];
  zoom: number;
  center: LonLat;
  onViewportChange: (value: { zoom: number; center: LonLat; bbox: BBox }) => void;
  onClusterSelect: (cluster: Cluster) => void;
  onResetView?: () => void;
  highlightedCaseId?: string;
  reducedMotion: boolean;
  tileState: "loading" | "ready" | "failed";
  onTileStateChange?: (state: "ready" | "failed") => void;
  boundaryHighlight?: string;
};

const FALLBACK_MARKER_SIZE = 100;
const FALLBACK_MARKER_GAP = 112;

export function fallbackClusterPoints(clusters: Cluster[]) {
  const placed: Array<{ cluster: Cluster; x: number; y: number }> = [];
  const grid = Array.from({ length: Math.floor((KOREA_MAP_HEIGHT - FALLBACK_MARKER_SIZE) / FALLBACK_MARKER_GAP) + 1 }, (_, row) =>
    Array.from({ length: Math.floor((KOREA_MAP_WIDTH - FALLBACK_MARKER_SIZE) / FALLBACK_MARKER_GAP) + 1 }, (_, column) => ({
      x: FALLBACK_MARKER_SIZE / 2 + column * FALLBACK_MARKER_GAP,
      y: FALLBACK_MARKER_SIZE / 2 + row * FALLBACK_MARKER_GAP,
    })),
  ).flat();
  for (const cluster of clusters) {
    const projected = projectKorea(cluster.lon, cluster.lat);
    const clamped = {
      x: Math.min(KOREA_MAP_WIDTH - FALLBACK_MARKER_SIZE / 2, Math.max(FALLBACK_MARKER_SIZE / 2, projected.x)),
      y: Math.min(KOREA_MAP_HEIGHT - FALLBACK_MARKER_SIZE / 2, Math.max(FALLBACK_MARKER_SIZE / 2, projected.y)),
    };
    const candidates = [clamped, ...grid].sort((a, b) => Math.hypot(a.x - projected.x, a.y - projected.y) - Math.hypot(b.x - projected.x, b.y - projected.y));
    const best = candidates.find((candidate) => placed.every((point) => Math.hypot(point.x - candidate.x, point.y - candidate.y) >= FALLBACK_MARKER_GAP)) ?? clamped;
    placed.push({ cluster, ...best });
  }
  return placed;
}

/** Screen-space placement: use the nearest free slot, never an overlapping origin fallback. */
export function screenClusterPoints(origins: Array<{x: number; y: number}>, width: number, height: number) {
  const gap = 52, margin = 26;
  const grid: Array<{x: number; y: number}> = [];
  for (let y = margin; y <= height - margin; y += gap) {
    for (let x = margin; x <= width - margin; x += gap) grid.push({x, y});
  }
  if (origins.length > grid.length) return null;
  const place = (gridOnly: boolean) => {
    const placed: Array<{x: number; y: number}> = [];
    for (const origin of origins) {
      const clamped = {x: Math.max(margin, Math.min(width - margin, origin.x)), y: Math.max(margin, Math.min(height - margin, origin.y))};
      const candidates = gridOnly ? grid : [clamped, ...grid];
      const point = [...candidates].sort((a,b) => Math.hypot(a.x-origin.x,a.y-origin.y)-Math.hypot(b.x-origin.x,b.y-origin.y))
        .find(p => placed.every(other => Math.hypot(p.x-other.x,p.y-other.y) >= gap));
      if (!point) return null;
      placed.push(point);
    }
    return placed;
  };
  // Natural positions can fragment the available space; a complete grid has a known capacity.
  return place(false) ?? place(true);
}

function clusterActionLabel(cluster: Cluster): string {
  return cluster.level === "case" ? "공개 정보 1건 상세 열기" : `${cluster.label} ${cluster.count}건 묶음 확대`;
}

function BoundaryFallbackMap({ clusters, onClusterSelect }: Pick<MapCanvasProps, "clusters" | "onClusterSelect">) {
  const [path, setPath] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let disposed = false;
    void Promise.all([import("d3-geo"), import("topojson-client"), import("../../../geo/src/succession")]).then(([{ geoPath }, { feature }, { SIGUNGU_TOPOLOGY }]) => {
      const boundaries = feature(SIGUNGU_TOPOLOGY as never, SIGUNGU_TOPOLOGY.objects.sigungu as never);
      const value = geoPath(createKoreaProjection())(boundaries as never);
      if (!disposed) setPath(value || null);
    }).catch(() => { if (!disposed) setFailed(true); });
    return () => { disposed = true; };
  }, []);
  if (failed) return <div className="boundary-fallback list-only"><p role="alert">축약 지도도 표시하지 못했어요. 같은 화면의 목록으로 모두 확인할 수 있어요.</p></div>;
  const points = fallbackClusterPoints(clusters);
  return <section className="boundary-fallback" aria-label="타일 없이 표시하는 행정구역 축약 지도">
    <svg viewBox={`0 0 ${KOREA_MAP_WIDTH} ${KOREA_MAP_HEIGHT}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label="대한민국 시군구 경계와 지역별 공개 건수">
      <path d={path ?? ""} />
      {points.map(({ cluster, x, y }) => {
        return <foreignObject key={cluster.key} x={x - FALLBACK_MARKER_SIZE / 2} y={y - FALLBACK_MARKER_SIZE / 2} width={FALLBACK_MARKER_SIZE} height={FALLBACK_MARKER_SIZE} data-cluster-key={cluster.key} data-map-x={x.toFixed(2)} data-map-y={y.toFixed(2)}>
          <button type="button" onClick={() => onClusterSelect(cluster)} aria-label={clusterActionLabel(cluster)}>{cluster.count}</button>
        </foreignObject>;
      })}
    </svg>
  </section>;
}

export function MapCanvas({ clusters, zoom, center, onViewportChange, onClusterSelect, onResetView, highlightedCaseId, reducedMotion, tileState, onTileStateChange, boundaryHighlight }: MapCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const callbacksRef = useRef({ onViewportChange, onClusterSelect, onTileStateChange });
  const viewRef = useRef({ zoom, center });
  const [mapReady, setMapReady] = useState(false);
  const [crowded, setCrowded] = useState(false);

  callbacksRef.current = { onViewportChange, onClusterSelect, onTileStateChange };
  viewRef.current = { zoom, center };

  useEffect(() => {
    let disposed = false;
    void (async () => {
      if (!containerRef.current || mapRef.current) return;
      const [maplibregl, { findArabomBackgroundSource, loadArabomMapStyle }, { SIGUNGU_TOPOLOGY }, { feature }] = await Promise.all([
        import("maplibre-gl"),
        import("../../../geo/src/map-style"),
        import("../../../geo/src/succession"),
        import("topojson-client"),
        import("maplibre-gl/dist/maplibre-gl.css"),
      ]);
      if (disposed || !containerRef.current) return;
      const style = await loadArabomMapStyle();
      if (disposed || !containerRef.current) return;
      const initial = viewRef.current;
      const map = new maplibregl.Map({ container: containerRef.current, center: [...initial.center], zoom: initial.zoom, maxZoom: 13, attributionControl: false, cooperativeGestures: true, fadeDuration: reducedMotion ? 0 : 300 });
      mapRef.current = map;
      const backgroundSourceId = findArabomBackgroundSource(style);
      let backgroundRendered = false;
      const markBackgroundReady = () => {
        if (disposed || backgroundRendered) return;
        backgroundRendered = true;
        callbacksRef.current.onTileStateChange?.("ready");
      };
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
      map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right");
      const publishViewport = () => {
        const bounds = map.getBounds();
        const currentCenter = map.getCenter();
        callbacksRef.current.onViewportChange({ zoom: map.getZoom(), center: [currentCenter.lng, currentCenter.lat], bbox: [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()] });
      };
      map.once("style.load", () => {
        if (disposed) return;
        const boundaries = feature(SIGUNGU_TOPOLOGY as never, SIGUNGU_TOPOLOGY.objects.sigungu as never);
        map.addSource("arabom-boundaries", { type: "geojson", data: boundaries as never });
        map.addLayer({ id: "arabom-boundary-lines", type: "line", source: "arabom-boundaries", paint: { "line-color": "#4f6b45", "line-width": ["case", ["==", ["get", "sigunguCode"], ""], 3, 0.8], "line-opacity": 0.75 } });
        setMapReady(true);
        publishViewport();
      });
      // MapLibre's global `load` waits for every active source. The current
      // OpenFreeMap vector source can keep parsing after its raster backdrop
      // has visibly rendered, so use a successfully loaded backdrop tile as
      // the first-value readiness signal. A failed/absent tile still reaches
      // the existing eight-second fallback unchanged.
      map.on("sourcedata", (event) => {
        if (event.sourceId === backgroundSourceId && event.tile?.state === "loaded") markBackgroundReady();
      });
      if (!backgroundSourceId) map.once("load", markBackgroundReady);
      map.on("moveend", publishViewport);
      // Register every style listener before supplying the object. Small
      // inline styles can emit `style.load` before a post-constructor listener
      // is attached, which would otherwise leave the readiness state loading.
      map.setStyle(style as never);
    })().catch(() => {
      containerRef.current?.setAttribute("data-runtime-failed", "true");
      callbacksRef.current.onTileStateChange?.("failed");
    });
    return () => {
      disposed = true;
      mapRef.current?.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, [reducedMotion]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const map = mapRef.current;
    const current = map.getCenter();
    if (Math.abs(map.getZoom() - zoom) < 0.01 && Math.abs(current.lng - center[0]) < 0.0001 && Math.abs(current.lat - center[1]) < 0.0001) return;
    map.easeTo({ center: [...center], zoom, duration: reducedMotion ? 0 : 320 });
  }, [center, mapReady, reducedMotion, zoom]);

  useEffect(() => {
    if (!mapReady || !mapRef.current?.getLayer("arabom-boundary-lines")) return;
    mapRef.current.setPaintProperty("arabom-boundary-lines", "line-width", ["case", ["==", ["get", "sigunguCode"], boundaryHighlight ?? ""], 3, 0.8]);
  }, [boundaryHighlight, mapReady]);

  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const markers: MapLibreMarker[] = [];
    let disposed = false;
    let cleanupLayout = () => {};
    void import("maplibre-gl").then((maplibregl) => {
      if (disposed || !mapRef.current) return;
      const map = mapRef.current;
      for (const cluster of clusters) {
        const element = document.createElement("button");
        element.type = "button";
        element.className = "cluster-mark";
        element.textContent = String(cluster.count);
        element.ariaLabel = clusterActionLabel(cluster);
        element.setAttribute("aria-pressed", String(cluster.caseIds.includes(highlightedCaseId ?? "")));
        element.addEventListener("click", () => callbacksRef.current.onClusterSelect(cluster));
        markers.push(new maplibregl.Marker({ element }).setLngLat([cluster.lon, cluster.lat]).addTo(mapRef.current!));
      }
      const positionMarkers = () => {
        const origins = clusters.map(cluster => map.project([cluster.lon, cluster.lat]));
        const points = screenClusterPoints(origins, map.getContainer().clientWidth, map.getContainer().clientHeight);
        setCrowded(points === null);
        markers.forEach((marker, index) => {
          marker.getElement().hidden = points === null;
          marker.getElement().style.display = points ? "" : "none";
          if (points) marker.setOffset([points[index]!.x-origins[index]!.x, points[index]!.y-origins[index]!.y]);
        });
      };
      positionMarkers();
      map.on("move", positionMarkers);
      map.on("resize", positionMarkers);
      cleanupLayout = () => { map.off("move", positionMarkers); map.off("resize", positionMarkers); };
    });
    return () => {
      disposed = true;
      cleanupLayout();
      for (const marker of markers) marker.remove();
    };
  }, [clusters, highlightedCaseId, mapReady]);

  const clusterStatus = clusters.length
    ? `지도에 ${clusters.length}개 ${zoom < 7 ? "시도" : zoom < 10 ? "시군구" : "개별"} 항목으로 ${clusters.reduce((sum, cluster) => sum + cluster.count, 0)}건을 표시해요.`
    : "현재 지도에 표시할 위치 정보가 없어요.";

  return <section className="map-canvas" aria-label="지역 단위 실종정보 지도" data-zoom={zoom} data-center={center.join(",")} data-cluster-count={clusters.length} data-reduced-motion={reducedMotion} data-tile-state={tileState} data-boundary-highlight={boundaryHighlight}>
    <p className="visually-hidden" role="status">{clusterStatus}</p>
    {zoom > 5 && onResetView && <button className="fallback-reset" type="button" onClick={onResetView}>전국 보기</button>}
    <div ref={containerRef} className="maplibre-runtime" />
    {crowded && tileState !== "failed" && <p className="map-loading" role="status">묶음이 많아 숫자를 겹치지 않고 표시할 공간이 부족해요. 아래 목록이나 지역 검색으로 확인해 주세요.</p>}
    {tileState === "loading" && <p className="map-loading" role="status">지도 배경을 불러오는 중이에요.</p>}
    {tileState === "failed" && <><p className="tile-failure-banner" role="alert">지도 배경을 불러오지 못해 같은 위치의 행정구역 경계로 바꿨어요. 아래 목록도 그대로 이용할 수 있어요.</p><BoundaryFallbackMap clusters={clusters} onClusterSelect={onClusterSelect}/></>}
  </section>;
}
